import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../context/ApiProvider';
import { useAuth } from '../context/AuthProvider';
import type { ScheduleMeetingType } from '../lib/schedule/meetingTypes';

export type ScheduleType = ScheduleMeetingType;

export interface ScheduleSlot {
  id: string;
  userId?: string;
  utcDateTime: string;
  localTime: string;
  localDate: string;
  timezone: string;
  type: ScheduleType;
  isActive: boolean;
  isRecurring: boolean;
  recurringGroupId?: string | null;
  recurringPattern?: 'weekly' | 'bi-weekly' | 'monthly' | null;
  metadata?: Record<string, unknown> | null;
  host?: {
    uid: string;
    name?: string | null;
    email?: string | null;
    role?: string | null;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ScheduleSettings {
  id?: string;
  isSuspended: boolean;
  suspensionReason?: string | null;
  suspendedUntil?: string | null;
  timezone: string;
  allowedMeetingTypes?: ScheduleType[];
  createdAt: string;
  updatedAt: string;
}

export interface ScheduleEventAttendee {
  id: string;
  userId?: string | null;
  email?: string | null;
  name?: string | null;
  role?: string | null;
  createdAt?: string;
}

export interface ScheduleEvent {
  id: string;
  orgId?: string | null;
  hostUserId: string;
  organizerUserId?: string | null;
  clientUserId?: string | null;
  slotId?: string | null;
  meetingType: ScheduleType;
  title: string;
  description?: string | null;
  startsAt: string;
  endsAt: string;
  timezone: string;
  status: 'scheduled' | 'canceled' | 'completed' | 'no_show';
  leadName?: string | null;
  leadEmail?: string | null;
  leadPhone?: string | null;
  meetingLinks?: string[];
  discordMeeting?: {
    scheduledEventId: string;
    scheduledEventUrl: string;
    textChannelId: string;
    textChannelUrl: string;
    voiceChannelId: string;
    voiceChannelName: string;
    voiceChannelUrl: string;
    messageId?: string | null;
    messageUrl?: string | null;
    syncedAt: string;
  } | null;
  attendees: ScheduleEventAttendee[];
  createdAt: string;
  updatedAt: string;
}

interface SlotsFilters {
  startDate?: string;
  endDate?: string;
  type?: ScheduleType;
}

export interface AvailabilityRule {
  id?: string;
  name: string;
  startDate: string;
  endDate?: string | null;
  times: string[];
  daysOfWeek: number[];
  pattern: 'weekly' | 'bi-weekly' | 'monthly';
  type: ScheduleType;
  timezone: string;
  isActive?: boolean;
}

export interface ScheduleBlackout {
  id: string;
  startsAt: string;
  endsAt: string;
  type: 'vacation' | 'blocked';
  reason?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface BookableSlotFilters extends SlotsFilters {
  hostUserId?: string;
}

export interface CreateScheduleEventInput {
  slotId?: string;
  orgId?: string;
  hostUserUid?: string;
  startsAt?: string;
  timezone?: string;
  meetingType?: ScheduleType;
  title?: string;
  description?: string;
  meetingLinks?: string[];
  durationMinutes?: number;
  clientUserUid?: string;
  attendees?: Array<{
    userUid?: string;
    userId?: string;
    email?: string;
    name?: string;
    role?: string;
  }>;
}

export interface CreatePublicScheduleEventInput {
  slotId: string;
  leadName: string;
  leadEmail: string;
  leadPhone?: string;
  title?: string;
  description?: string;
}

export function useRules(enabled = true) {
  const api = useApi();
  const { loading } = useAuth();
  return useQuery<AvailabilityRule[]>({
    queryKey: ['schedule', 'rules'],
    queryFn: () => api.get('/schedule/rules').then((r) => r.data),
    enabled: !loading && enabled,
    staleTime: 5 * 60_000,
  });
}

export function useCreateRule() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rule: Omit<AvailabilityRule, 'id' | 'isActive'>) =>
      api.post('/schedule/rules', rule).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule', 'rules'] });
      qc.invalidateQueries({ queryKey: ['schedule', 'slots'] });
    },
  });
}

export function useUpdateRule() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<AvailabilityRule>;
    }) => api.patch(`/schedule/rules/${id}`, updates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule', 'rules'] });
      qc.invalidateQueries({ queryKey: ['schedule', 'slots'] });
    },
  });
}

export function useDeleteRule() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/schedule/rules/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule', 'rules'] });
      qc.invalidateQueries({ queryKey: ['schedule', 'slots'] });
    },
  });
}

export function useScheduleMeetingTypes(enabled = true) {
  const api = useApi();
  const { loading } = useAuth();
  return useQuery({
    queryKey: ['schedule', 'meeting-types'],
    queryFn: () => api.get('/schedule/meeting-types').then((r) => r.data),
    enabled: !loading && enabled,
    staleTime: 5 * 60_000,
  });
}

export function useScheduleSettings(enabled = true) {
  const api = useApi();
  const { loading } = useAuth();
  return useQuery<ScheduleSettings>({
    queryKey: ['schedule', 'settings'],
    queryFn: () => api.get('/schedule/settings').then((r) => r.data),
    enabled: !loading && enabled,
    staleTime: 5 * 60_000,
  });
}

export function useUpdateScheduleSettings() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (updates: Partial<ScheduleSettings>) =>
      api.patch('/schedule/settings', updates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule', 'settings'] });
    },
  });
}

export function useScheduleSlots(filters: SlotsFilters = {}, enabled = true) {
  const api = useApi();
  const { loading } = useAuth();

  return useQuery<ScheduleSlot[]>({
    queryKey: ['schedule', 'slots', filters],
    queryFn: () =>
      api.get('/schedule/slots', { params: filters }).then((r) => r.data),
    enabled: !loading && enabled,
    staleTime: 2 * 60_000,
  });
}

export function useCreateScheduleSlots() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slots: Partial<ScheduleSlot>[]) =>
      api.post('/schedule/slots', { slots }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule', 'slots'] });
    },
  });
}

export function useUpdateScheduleSlot() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      slotId,
      updates,
    }: {
      slotId: string;
      updates: Partial<ScheduleSlot>;
    }) => api.patch(`/schedule/slots/${slotId}`, updates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule', 'slots'] });
    },
  });
}

export function useDeleteScheduleSlot() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slotId: string) => api.delete(`/schedule/slots/${slotId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule', 'slots'] });
    },
  });
}

export function useScheduleBlackouts(enabled = true) {
  const api = useApi();
  const { loading } = useAuth();
  return useQuery<ScheduleBlackout[]>({
    queryKey: ['schedule', 'blackouts'],
    queryFn: () => api.get('/schedule/blackouts').then((r) => r.data),
    enabled: !loading && enabled,
  });
}

export function useCreateScheduleBlackout() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      startsAt: string;
      endsAt: string;
      type: 'vacation' | 'blocked';
      reason?: string;
    }) => api.post('/schedule/blackouts', payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule', 'blackouts'] });
      qc.invalidateQueries({ queryKey: ['schedule', 'slots'] });
    },
  });
}

export function useDeleteScheduleBlackout() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (blackoutId: string) =>
      api.delete(`/schedule/blackouts/${blackoutId}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule', 'blackouts'] });
      qc.invalidateQueries({ queryKey: ['schedule', 'slots'] });
    },
  });
}

export function useBookableScheduleSlots(
  filters: BookableSlotFilters = {},
  enabled = true,
) {
  const api = useApi();
  const { loading } = useAuth();
  return useQuery<ScheduleSlot[]>({
    queryKey: ['schedule', 'bookable-slots', filters],
    queryFn: () =>
      api.get('/schedule/bookable-slots', { params: filters }).then((r) => r.data),
    enabled: !loading && enabled,
    staleTime: 60_000,
  });
}

export function useScheduleEvents(enabled = true) {
  const api = useApi();
  const { loading } = useAuth();
  return useQuery<ScheduleEvent[]>({
    queryKey: ['schedule', 'events'],
    queryFn: () => api.get('/schedule/events').then((r) => r.data),
    enabled: !loading && enabled,
    staleTime: 30_000,
  });
}

export function useCreateScheduleEvent() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateScheduleEventInput) =>
      api.post('/schedule/events', payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule', 'events'] });
      qc.invalidateQueries({ queryKey: ['schedule', 'bookable-slots'] });
      qc.invalidateQueries({ queryKey: ['schedule', 'slots'] });
    },
  });
}

export function useCreatePublicScheduleEvent() {
  const api = useApi();
  return useMutation({
    mutationFn: (payload: CreatePublicScheduleEventInput) =>
      api.post('/public/events', payload).then((r) => r.data),
  });
}

export function useScheduleForMonth(year: number, month: number) {
  const startDate = `${year}-${month.toString().padStart(2, '0')}-01`;
  const endDate = `${year}-${month.toString().padStart(2, '0')}-31`;
  return useScheduleSlots({ startDate, endDate });
}

export function useScheduleForDateRange(startDate: string, endDate: string) {
  return useScheduleSlots({ startDate, endDate });
}

export function useScheduleByType(type: ScheduleType) {
  return useScheduleSlots({ type });
}
